// src/utils/employeeDataProcessing.js
import { CURRENT_DATE } from './constants';
import { Logger } from './logger';

const logger = new Logger('employeeDataProcessing');

/**
 * Format availability text from employee availability object
 * @param {Object} employeeData - Employee data containing availability_object
 * @returns {string} - Formatted availability text
 */
export const formatAvailabilityFromObject = (employeeData) => {
  const availabilityObj = employeeData.availability_object;

  // If no availability object, check for fallback indicators
  if (!availabilityObj) {
    // If they have a next_available_date in the employee data, they're busy
    if (employeeData.next_available_date) {
      const nextAvailable = employeeData.next_available_date;
      try {
        if (typeof nextAvailable === 'string') {
          if (nextAvailable.includes(',') && !nextAvailable.includes('T')) {
            return nextAvailable;
          } else if (nextAvailable.includes('T')) {
            const dateObj = new Date(nextAvailable);
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: dateObj.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
              });
            }
          }
        }
        return nextAvailable;
      } catch (error) {
        logger.error('Error formatting fallback availability date:', error);
        return nextAvailable;
      }
    }
    // No availability object and no next_available_date means they're on the bench
    return "Now";
  }

  const status = availabilityObj.status;
  const reason = availabilityObj.reason;
  const nextAvailable = availabilityObj.next_available_date;

  if (status === 'AVAILABLE') {
    // If they're marked as available, they're on the bench
    return "Now";
  } else if (status === 'BUSY' && nextAvailable) {
    try {
      // ENHANCED: Handle multiple date formats and show actual availability dates
      if (typeof nextAvailable === 'string') {
        if (nextAvailable.includes(',') && !nextAvailable.includes('T')) {
          // Already formatted like "Dec 23, 2024" - use as is
          return nextAvailable;
        } else if (nextAvailable.includes('T')) {
          // ISO date format
          const dateObj = new Date(nextAvailable);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: dateObj.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
            });
          }
        } else {
          // Try to parse as date
          const dateObj = new Date(nextAvailable);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: dateObj.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
            });
          }
        }
      } else if (nextAvailable instanceof Date) {
        return nextAvailable.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: nextAvailable.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
      }

      // Fallback to original format if parsing fails
      return nextAvailable;
    } catch (error) {
      logger.error('Error formatting availability date:', error);
      return reason || status || "Unknown";
    }
  } else if (status === 'BUSY') {
    // ENHANCED: If busy but no specific date, show "Busy" instead of just status
    return "Busy";
  } else {
    return reason || status || "Unknown";
  }
};

/**
 * Filter engagements to only include current ones (end_date after CURRENT_DATE or no end_date)
 * @param {Array} engagements - Array of engagement objects
 * @returns {Array} - Filtered array of current engagements
 */
export const filterCurrentEngagements = (engagements) => {
  return engagements.filter(engagement => {
    if (!engagement.end_date) return true;

    const endDate = engagement.end_date instanceof Date
      ? engagement.end_date
      : new Date(engagement.end_date);

    return endDate > CURRENT_DATE;
  });
};

/**
 * Process employee basic data from search results
 * @param {Object} employee - Raw employee data from search
 * @returns {Object} - Processed employee data
 */
export const processEmployeeBasicData = (employee) => ({
  // Basic information from search results
  employee_id: employee.employee_id || `${Date.now()}`,
  first_name: employee.first_name || 'Unknown',
  last_name: employee.last_name || 'Employee',
  email: employee.email || null,
  title: employee.title || 'N/A',
  department: employee.department || 'N/A',
  organizational_unit: employee.department || employee.organizational_unit || 'N/A',
  manager_name: employee.manager_name || 'N/A',
  manager_id: employee.manager_id || null,

  // Availability from search results (direct field)
  availability_object: employee.availability,
  availability_status: employee.availability?.status ||
                      employee.mbco_claude_employee_information?.availability_status,
  availability_percentage: employee.mbco_claude_employee_information?.availability_percentage,
  next_available_date: employee.availability?.next_available_date ||
                      employee.mbco_claude_employee_information?.next_available_date,
  current_status: employee.mbco_claude_employee_information?.current_status ||
                 employee.mbco_claude_employee_basic?.current_status ||
                 employee.current_status
});
